import React, { FunctionComponent } from "react";
import { AccordionItemData, AccordionListPanel } from "../../../generic/accordion/AccordionListPanel";
import { AddTagModal } from "./AddTagModal";
import { ObjectListItemRow } from "../../../coobjects/object-list-panel/ObjectListItemRow";
import { TagsPanelProps, TagsPanelResult, useTagsPanel } from "./TagsPanelHook";
import { formatMessage, formatMessageWithParams } from "../../../../utils/helpers/LanguageHelper";
import { Tag } from "../../../interfaces/Tag";
import FooterPanel from "../../../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { OperationCompletedPanel } from "../../../layout/OperationCompletedPanel";
import { CoObjectTypeEnum } from "../../../../api/models";
import { CustomModal } from "../../../layout/ActionModal";
import { renderCellTitle } from "../../../../utils/helpers/TagsHelper";
import { CellAddress } from "../../../interfaces/CoObject";

export const TagsPanel: FunctionComponent<TagsPanelProps> = (props) => {
  const { coObjects, updateTags } = props;
  const {
    tags,
    filteredTags,
    expanded,
    showAddTagsModal,
    setShowAddTagsModal,
    search,
    handleAccordionToggle,
    handleObjectClick,
    handleFilter,
    actionGroups,
    tagToEdit,
    setTagToEdit,
    deleteConfirm,
    tagToDelete,
    confirmDelete,
    cancelDelete,
  }: TagsPanelResult = useTagsPanel(props);

  const mapTagToAccordionItem = (tag: Tag): AccordionItemData<Tag> => ({
    id: tag.id,
    title: tag.label,
    data: tag,
  });

  const accordionItems = tags.map(mapTagToAccordionItem);
  const filteredAccordionItems = filteredTags.map(mapTagToAccordionItem);

  const renderItemBadge = (item: AccordionItemData<Tag>) => (
    <span
      style={{
        marginLeft: 8,
        background: "var(--color-blue)",
        color: "white",
        borderRadius: 8,
        padding: "2px 8px",
        fontSize: 10,
        flexShrink: 0,
      }}
    >
      {item.data.objects.length} objects
    </span>
  );

  const renderItemContent = (item: AccordionItemData<Tag>, isExpanded: boolean) => {
    // lazy rendering
    if (!isExpanded) {
      return null;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", rowGap: 4, paddingBottom: "12px" }}>
        {item.data.objects.map((obj) => {
          let objectName = obj.objectName;

          if (obj.objectType === CoObjectTypeEnum.TABLE_CELL) {
            const address: CellAddress = { row: obj.row, column: obj.column };
            objectName = `${obj.objectName} ${renderCellTitle(address, undefined, true)}`;
          }

          const objectItem = {
            documentId: obj.documentId ?? props.documentId,
            contentControlId: obj.contentControlId,
            id: obj.id,
            info: {
              objectName,
              objectType: obj.objectType,
              info: "",
            },
          };
          const commonProps = {
            key: obj.id,
            objectItem,
            onClick: () => handleObjectClick(obj),
            selectable: false,
            showStatusIcons: false,
          };

          // eslint-disable-next-line react/jsx-key
          return <ObjectListItemRow {...commonProps} />;
        })}
      </div>
    );
  };

  const renderFooter = (
    <FooterPanel>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton
          text={formatMessage("cof.validation.button.createnewtag")}
          onClick={() => {
            setTagToEdit(null);
            setShowAddTagsModal(true);
          }}
        />
      </div>
      {showAddTagsModal && (
        <AddTagModal
          coObjects={coObjects}
          tags={tags}
          updateTags={updateTags}
          updateRules={props.updateRules}
          isShown={showAddTagsModal}
          onHide={() => setShowAddTagsModal(false)}
          selectedTag={tagToEdit}
        />
      )}
    </FooterPanel>
  );

  const renderEmptyContent = (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <OperationCompletedPanel
        message={formatMessage("cof.validation.title.notagyet")}
        alt={formatMessage("cof.validation.title.notagyet")}
        icon={"/assets/illustrations/notFound.svg"}
      />
    </div>
  );

  return (
    <>
      {deleteConfirm && tagToDelete && (
        <CustomModal
          modalTitle={formatMessageWithParams("cof.validation.title.tagnamedelete", {
            tagName: tagToDelete.label,
          })}
          messageTitle={formatMessageWithParams("cof.validation.message.confirm.deletetag", {
            tagName: tagToDelete.label,
          })}
          message={undefined}
          type="delete"
          onHide={() => {}}
          actions={[
            { title: formatMessage("cof.base.noconfirmdelete"), onClick: cancelDelete, variant: "blue" },
            { title: formatMessage("cof.base.confirmdelete"), onClick: confirmDelete, variant: "red_filled" },
          ]}
        />
      )}
      <AccordionListPanel
        items={accordionItems}
        filteredItems={filteredAccordionItems}
        expanded={expanded}
        searchQuery={search}
        actionGroups={actionGroups}
        onAccordionToggle={handleAccordionToggle}
        onFilter={handleFilter}
        itemBadge={renderItemBadge}
        itemContent={renderItemContent}
        footer={renderFooter}
        emptyContent={renderEmptyContent}
        noItemsFoundMessage="No tags found"
        automationIdSuffix="tag"
      />
    </>
  );
};
